// @flow

import { useEffect, useMemo } from 'react';
import { type Tenant } from '@tectonics/commons/tenant';
import { useLocalTenancyAsTenant } from '@tectonics/controller/context';
import { shouldCheckForPaymentConfirmation } from '@tectonics/prs/merchant/config';
import { useIncomingPayment } from './context';
import { fetchIncomingPayment } from './fetch-incoming-payment';
import { getHash } from './get-hash';
import { getMerchant } from './get-merchant';
import { getStatus } from './get-status';

export function useReloadAfterPaymentGetsConfirmedOrCanceled() {
  const tenant = useLocalTenancyAsTenant();
  const incomingPayment = useIncomingPayment();
  const paymentHash = useMemo(() => getHash(incomingPayment), [incomingPayment]);
  const merchant = useMemo(() => getMerchant(incomingPayment), [incomingPayment]);

  useEffect(() => {
    let interval: IntervalID;

    if (shouldCheckForPaymentConfirmation(merchant)) {
      interval = setInterval(async () => {
        await queryPaymentAndReloadIfNotPending(paymentHash, interval, tenant);
      }, 10000);
    }

    return () => clearInterval(interval);
  }, [merchant, paymentHash, tenant]);
}

async function queryPaymentAndReloadIfNotPending(paymentHash: string, interval: IntervalID, tenant: Tenant) {
  // Purposefully ignoring errors as this function is called inside setInterval and will be executed again
  const payment = await fetchIncomingPayment(paymentHash, tenant)
    .catch(() => null);

  if (payment === null) {
    return;
  }

  const status = getStatus(payment);

  if (status !== 'PE') {
    clearInterval(interval);
    window.location.reload();
  }
}
